/**
 * 给 media gateway URL 附加访问令牌
 *
 * 浏览器 <video> / <img> 标签无法自动带 Authorization header，
 * 后端 /api/media/file/* 路由支持 query 参数鉴权。
 *
 * 安全约束：URL 里只允许出现短时媒体令牌（aud=media，默认 30 分钟）。
 * 完整账号 JWT 严禁进 URL（会泄漏进访问日志 / Referer）——包括剥离
 * 历史遗留的 ?token= 参数，杜绝旧快照把长效凭据带回浏览器地址层。
 *
 * 令牌刷新策略：每次解析都用当前缓存的新鲜媒体令牌整体替换 URL 上的
 * 旧令牌（持久化快照里的过期 token 在渲染时自愈）；缓存未就绪时保留
 * URL 上已有的 mediaToken（同为短时媒体令牌）。
 *
 * DTO 只应给出 /api/media/file/{key}；外部 URL 不是业务展示输出。
 */

import { getMediaToken } from './mediaToken';

/** 剥离 query 中的令牌参数；keepMediaToken=false 时 mediaToken 也一并剥掉，其余参数（download=1 等）保留 */
function stripQueryTokens(url: string, keepMediaToken: boolean): string {
  const queryIndex = url.indexOf('?');
  if (queryIndex < 0) return url;
  const hashIndex = url.indexOf('#', queryIndex);
  const query = url.slice(queryIndex + 1, hashIndex < 0 ? undefined : hashIndex);
  const rest = hashIndex < 0 ? '' : url.slice(hashIndex);
  const kept = query.split('&').filter((pair) => {
    const key = pair.split('=')[0];
    if (key === 'token') return false;
    if (key === 'mediaToken') return keepMediaToken;
    return Boolean(pair);
  });
  return kept.length ? `${url.slice(0, queryIndex)}?${kept.join('&')}${rest}` : `${url.slice(0, queryIndex)}${rest}`;
}

/**
 * 全站唯一浏览器媒体 URL 入口。
 * 支持 canonical key、相对/绝对 /api/media/file URL（绝对地址统一归一化为相对路径，
 * 保证局域网/跨主机访问可达）、外部 URL、data URL。
 */
export function resolveBrowserMediaUrl(
  value: string | null | undefined,
  options: { download?: boolean } = {}
): string {
  if (!value) return '';
  let url = String(value).trim();
  if (!url) return '';
  if (/^(images|videos)\//.test(url)) {
    const encoded = url.split('/').map(encodeURIComponent).join('/');
    url = `/api/media/file/${encoded}`;
  }
  // 绝对地址的媒体网关 URL 归一化为相对路径。
  // 后端按 SERVER_URL/请求 Host 拼出绝对地址（如 http://localhost:3001/api/...），
  // 局域网设备解析不到该 host；剥掉 origin 后请求落在当前页面 origin（开发态走 Vite proxy 回源）。
  // 只处理网关路径，MinIO 预签名等真正的外部 URL 原样保留；query 中的令牌由下方统一处理。
  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url);
      if (parsed.pathname.includes('/api/media/file/')) {
        url = `${parsed.pathname}${parsed.search}${parsed.hash}`;
      }
    } catch {
      // 非法 URL 原样交给后续分支处理
    }
  }
  if (options.download && url.includes('/api/media/file/') && !/[?&]download=1(?:&|$)/.test(url)) {
    url += url.includes('?') ? '&' : '?download=1';
  }
  if (!url.includes('/api/media/file/')) return url;

  const mediaToken = getMediaToken();
  if (mediaToken) {
    // 新鲜媒体令牌整体替换旧令牌：持久化快照里的过期 token 在此自愈
    const stripped = stripQueryTokens(url, false);
    const separator = stripped.includes('?') ? '&' : '?';
    return `${stripped}${separator}mediaToken=${encodeURIComponent(mediaToken)}`;
  }
  // 缓存未就绪：保留 URL 上已有的 mediaToken（短时媒体令牌）；账号 JWT 一律剥除
  return stripQueryTokens(url, true);
}

/** Backward-compatible alias; all new callers should use resolveBrowserMediaUrl. */
export function withAuthToken(url: string | null | undefined): string {
  return resolveBrowserMediaUrl(url);
}
